'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import LoadingSpinner from 'components/v2/atoms/loading-spinner';

interface InfinityScrollProps {
  children: React.ReactNode;
  hasNextPage: boolean;
  isLoading: boolean;
  isError?: boolean;
  fetchNextPage: () => void;
  threshold?: number; // Distance from bottom to trigger load (in pixels)
  loadingComponent?: React.ReactNode;
  errorMessage?: React.ReactNode;
  className?: string;
}

const InfinityScroll = ({
  children,
  hasNextPage,
  isLoading,
  isError = false,
  fetchNextPage,
  threshold = 50,
  loadingComponent,
  errorMessage,
  className = '',
}: InfinityScrollProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);
  const handleScroll = useCallback(() => {
    if (
      !containerRef.current ||
      isLoadingRef.current ||
      !hasNextPage ||
      isError
    ) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom <= threshold) {
      isLoadingRef.current = true;
      fetchNextPage();
    }
  }, [hasNextPage, threshold, fetchNextPage, isError]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Use passive listener for better performance
    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // Also check for intersection observer for better performance (optional)
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loadMoreTriggerRef.current || !hasNextPage || isError) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && !isLoadingRef.current) {
          isLoadingRef.current = true;
          fetchNextPage();
        }
      },
      {
        threshold: 0.1,
        rootMargin: `${threshold}px`,
      },
    );

    observer.observe(loadMoreTriggerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, threshold, fetchNextPage, isError]);

  const defaultLoadingComponent = (
    <div className="relative flex h-40 items-center justify-center py-8">
      <LoadingSpinner width={40} height={40} />
    </div>
  );

  // const defaultEndMessage = (
  //   <div className="flex justify-center items-center py-8 text-gray-400">
  //     <span>すべてのデータを読み込みました</span>
  //   </div>
  // );

  const defaultErrorMessage = (
    <div className="flex items-center justify-center py-8 text-red-400">
      <span>データの読み込み中にエラーが発生しました</span>
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={`h-full overflow-y-auto ${className}`}
      style={{ scrollBehavior: 'smooth' }}
    >
      {children}

      {/* Intersection Observer trigger */}
      {hasNextPage && !isError && (
        <div ref={loadMoreTriggerRef} className="h-1" />
      )}

      {/* Loading state */}
      {isLoading && (loadingComponent || defaultLoadingComponent)}

      {/* Error state */}
      {isError && (errorMessage || defaultErrorMessage)}

      {/* End message */}
      {/* {!hasNextPage && !isLoading && !isError && endMessage} */}
    </div>
  );
};

export default InfinityScroll;
